import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { Layout, Typography, Table } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import axios from "api/axios";
import { formatDataGeneral, getAttributeValue } from "utils/utils";
import { transformDpatTableRows, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";

const { Content } = Layout;
const { Title } = Typography;

const DISTRICT_GENERAL_PROGRAM = "RwWtjFaorvN";
const PUBLICATIONS_PROGRAM = "X5kGqVpbGoN";
const SOCIAL_SERVICES_DOC_TYPE = "Social Services";

function normalizeWebsiteUrl(url) {
  if (!url || url === "N/A") {
    return "Not Uploaded";
  }
  const trimmed = String(url).trim();
  if (!trimmed) {
    return "Not Uploaded";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

const DistrictHotlineNumber = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [dataTable, setDataTable] = useState([]);
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(1);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI4",
      area: "Social Protection, Gender and Nutrition",
      maxScore,
      score,
      dataTable,
    }),
  }));

  const DPAT_SLUG = "sdi4.0-4.2";

  const districtHotlineNumberColumn = [
    { title: "Dedicated hotline exist (Yes/No)", dataIndex: "hotline", key: "hotline" },
    { title: "Hotline Number", dataIndex: "number", key: "number" },
    { title: "Website Link", dataIndex: "website", key: "website" },
  ];

  const loadFromDhis2 = useCallback(() => {
    Promise.all([
      axios.get(`/tracker/trackedEntities?orgUnit=${districtId}&program=${DISTRICT_GENERAL_PROGRAM}`),
      axios.get(`/tracker/trackedEntities?orgUnit=${districtId}&program=${PUBLICATIONS_PROGRAM}`),
    ])
      .then(([districtGeneralRes, publicationsRes]) => {
        const districtGeneral = districtGeneralRes.data.instances || [];
        const publications = publicationsRes.data.instances || [];
        const socialServicePublications = formatDataGeneral(
          publications,
          "Document Published",
          SOCIAL_SERVICES_DOC_TYPE,
        ) || [];

        const websiteUrl = socialServicePublications.length > 0
          ? normalizeWebsiteUrl(getAttributeValue("Website", socialServicePublications[0]))
          : "Not Uploaded";

        const temp = [];
        let tempScore = 0;

        if (districtGeneral.length > 0) {
          const hotlineNumber = getAttributeValue("Public Hotline", districtGeneral[0]);
          const hasHotline = hotlineNumber !== "N/A" && String(hotlineNumber).trim() !== "";

          temp.push({
            hotline: hasHotline ? "YES" : "NO",
            number: hasHotline ? hotlineNumber : "N/A",
            website: websiteUrl,
          });

          if (hasHotline && isEvidenceLinkUploaded(websiteUrl)) {
            tempScore = 1;
          }
        } else {
          temp.push({
            hotline: "NO",
            number: "N/A",
            website: websiteUrl,
          });
        }

        setScore(tempScore);
        setDataTable(temp);
      })
      .catch((err) => console.log("district hotline error ", err));
  }, [districtId]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.dataTable) setDataTable(snap.dataTable);
      if (snap.score != null) setScore(snap.score);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const linkLabels = { website: "View Website" };

  return (
    <Comment
      data={dataTable}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi4.0-4.2-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "20px" }}
            dhis2ProgramId={DISTRICT_GENERAL_PROGRAM}
            movDefaultText="District General Tracker on DDDP and Publications Tracker (Website)."
          />
          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.2 Actual Score:" score={score} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>
            Evidence of Dedicated Functional Hotline for Vulnerable Groups
          </Title>

          <Table
            columns={districtHotlineNumberColumn}
            dataSource={transformDpatTableRows(dataTable, ["website"], linkLabels)}
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "20px" }}>Conclusion</Title>
          <Content>
            {score === 1
              ? "There is a dedicated hot line in the District for vulnerable groups"
              : "There is no dedicated hot line in the District for vulnerable groups"}
          </Content>
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default DistrictHotlineNumber;
